/** The sandbox's outbound mode and tester allowlist (S3; SBX D5, O17, O19, O28) — ops' editor
 *  (`console_sandbox_manage`); the sandbox's own admins have theirs in its Developer section.
 *
 *  The hard limits the UI explains and the API enforces: allow-all is impossible while the sandbox's
 *  vendor key matches a production key (the reason comes from the server); our own domains are always
 *  on the list; public mailbox and reserved/test domains are refused; SMS and WhatsApp reach an
 *  allowlisted phone only once the sandbox has its own Twilio number; an ops suspension overrides any
 *  mode. Each save is audited with its comment. */
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  sandboxApi,
  sandboxErrorMessage,
  sandboxKeys,
  type OutboundMode,
  type SandboxAllowlist,
  type SandboxState,
} from '@/api/sandbox';
import { useSnackbar } from '@/components/Snackbar';

import { AllowlistEditor, allowlistErrorsOf, fullAllowlist, type ListKey } from './AllowlistEditor';
import { OUTBOUND_MODE_HELP, OUTBOUND_MODE_LABEL, OUTBOUND_MODES } from './sandboxWords';

interface OutboundValues {
  mode: OutboundMode;
  allowlist: Required<SandboxAllowlist>;
  comment: string;
}

function seed(state: SandboxState): OutboundValues {
  return {
    mode: state.outbound?.mode ?? 'outbox',
    allowlist: fullAllowlist(state.outbound?.allowlist),
    comment: '',
  };
}

/** The mode picker: one radio per mode with what it does; allow-all disabled with the server's reason. */
export function OutboundModeRadios({
  value,
  onChange,
  disabled,
  allowAllRefusal,
}: {
  value: OutboundMode;
  onChange: (mode: OutboundMode) => void;
  disabled?: boolean;
  /** Why allow-all is impossible now (O17), or null. */
  allowAllRefusal?: string | null;
}) {
  return (
    <RadioGroup
      value={value}
      onChange={(e) => onChange(e.target.value as OutboundMode)}
      aria-label="Outbound mode"
    >
      {OUTBOUND_MODES.map((m) => {
        const refused = m === 'allow_all' && Boolean(allowAllRefusal);
        return (
          <FormControlLabel
            key={m}
            value={m}
            disabled={disabled || refused}
            control={<Radio size="small" />}
            sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {OUTBOUND_MODE_LABEL[m]}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {refused ? `Not possible now: ${allowAllRefusal}` : OUTBOUND_MODE_HELP[m]}
                </Typography>
              </Box>
            }
          />
        );
      })}
    </RadioGroup>
  );
}

export function OutboundSection({
  topId,
  state,
  canManage,
}: {
  topId: number;
  state: SandboxState;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [listErrors, setListErrors] = useState<Partial<Record<ListKey, string>>>({});
  // Keyed by the stored values, not the response object: an unrelated refetch keeps the edits.
  const storedKey = JSON.stringify(state.outbound ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => seed(state), [storedKey]);
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<OutboundValues>({ defaultValues: initial });

  // A refetch (after this save, or someone else's) re-seeds the form.
  useEffect(() => {
    reset(initial);
  }, [initial, reset]);

  const save = useMutation({
    mutationFn: (v: OutboundValues) =>
      sandboxApi.updateOutbound(topId, {
        outbound: { mode: v.mode, allowlist: v.allowlist },
        comment: v.comment.trim() || undefined,
      }),
    onSuccess: (next) => {
      setListErrors({});
      qc.setQueryData(sandboxKeys.state(topId), next);
      snackbar.show('Outbound settings saved.', 'success');
    },
    onError: (e) => {
      setListErrors(allowlistErrorsOf(e));
      snackbar.show(sandboxErrorMessage(e), 'error');
    },
  });

  const suspended = state.suspension?.suspended;
  return (
    <Box
      component="form"
      onSubmit={handleSubmit((v) => save.mutate(v))}
      sx={{ display: 'grid', gap: 2 }}
    >
      {suspended && (
        <Alert severity="info">The sandbox is suspended: nothing is sent, whatever the mode.</Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1.3fr)' },
        }}
      >
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            Mode
          </Typography>
          <Controller
            name="mode"
            control={control}
            render={({ field }) => (
              <OutboundModeRadios
                value={field.value}
                onChange={field.onChange}
                disabled={!canManage}
                allowAllRefusal={
                  state.allow_all_allowed === false
                    ? (state.allow_all_refused_reason ?? 'the vendor key matches a production key')
                    : null
                }
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
            Reserved and test destinations (example.com, .test, .invalid, …) are kept in the outbox
            in every mode.
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            Allowlist
          </Typography>
          <Controller
            name="allowlist"
            control={control}
            render={({ field }) => (
              <AllowlistEditor
                value={field.value}
                onChange={field.onChange}
                ownDomains={state.own_domains ?? []}
                disabled={!canManage}
                errors={listErrors}
              />
            )}
          />
        </Box>
      </Box>
      {canManage && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label="Comment"
            placeholder="Why — kept in the audit record"
            inputProps={{ maxLength: 250 }}
            sx={{ flex: 1, maxWidth: 480 }}
            {...register('comment')}
          />
          <Button type="submit" variant="contained" disabled={!isDirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save outbound'}
          </Button>
          {isDirty && (
            <Button color="inherit" disabled={save.isPending} onClick={() => reset(initial)}>
              Discard
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
